import React, { useEffect, useState } from 'react';
import { SkeletonBlock } from '../../../components/skeleton/skeleton';
import dayjs from 'dayjs';
import ReactApexChart from 'react-apexcharts';
import {
  BanknotesIcon,
  ShoppingBagIcon,
  HandThumbUpIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';
import dashboardApi from '../../../services/dashboardApi';
import { setStartAndEndDate } from '../../../utils/setStartAndEndDate';
import { PageHeader, StatCard, SectionCard } from '../../../components/ui';
import { formatCurrency } from '../../../utils/baseUtils';

const basePieChartOptions = {
  chart: { width: 380, type: 'pie' },
  legend: { show: false },
  labels: [],
  stroke: { colors: ['transparent'] },
  responsive: [{ breakpoint: 480, options: { chart: { width: '100%' } } }],
  noData: {
    text: 'Loading or no data available…',
    align: 'center',
    verticalAlign: 'middle',
    offsetX: 0,
    offsetY: 0,
    style: { color: '#CBD5E1', fontSize: '14px' },
  },
};

const LiveTimeReport = () => {
  const [liveTimeReports, setLiveTimeReports] = useState({});
  const [loading, setLoading] = useState({ liveTimeLoading: true });
  const [totalOrdersPieChart, setTotalOrdersPieChart] = useState({
    series: [],
    chartOptions: basePieChartOptions,
  });
  const [totalSalesPieChart, setTotalSalesPieChart] = useState({
    series: [],
    chartOptions: basePieChartOptions,
  });

  const getLiveTimeReports = async () => {
    setLoading({ liveTimeLoading: true });
    try {
      const body = {
        startdate: setStartAndEndDate(dayjs(0), 'startdate', '0'),
        enddate: setStartAndEndDate(dayjs(), 'enddate', '0'),
      };
      const res = await dashboardApi.getReports(body);
      setLiveTimeReports(res.data || {});

      if (res.data?.counts?.length > 0) {
        const newData = res.data.counts.map((item) => ({
          ordercount: item.ordercount,
          ordertotal: item.ordertotal,
          companyname: item.companyname || 'No Company Name',
        }));

        setTotalOrdersPieChart({
          series: newData.map((item) => item.ordercount),
          chartOptions: {
            ...basePieChartOptions,
            labels: newData.map((item) => item.companyname),
          },
        });

        setTotalSalesPieChart({
          series: newData.map((item) => parseInt(item.ordertotal, 10)),
          chartOptions: {
            ...basePieChartOptions,
            labels: newData.map((item) => item.companyname),
          },
        });
      } else {
        setTotalOrdersPieChart({
          series: [],
          chartOptions: basePieChartOptions,
        });
        setTotalSalesPieChart({
          series: [],
          chartOptions: basePieChartOptions,
        });
      }
    } catch (error) {
      setLiveTimeReports({});
    } finally {
      setLoading({ liveTimeLoading: false });
    }
  };

  useEffect(() => {
    getLiveTimeReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-white dark:bg-slate-800">
      <div className="mx-auto max-w-5xl space-y-8">
        {loading.liveTimeLoading ? (
          <div className="w-full py-6 space-y-4">
            <SkeletonBlock className="h-6 w-48" />
            <SkeletonBlock className="h-80 w-full" />
          </div>
        ) : (
          <>
            <PageHeader
              title="Live Time Reports"
              description="Real-time sales metrics and performance."
              backTo="/dashboard/reports"
              backLabel="Back to Reports"
            />

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatCard
                icon={BanknotesIcon}
                tone="blue"
                label="Live Time Sales"
                value={formatCurrency(liveTimeReports.ordertotal)}
              />
              <StatCard
                icon={ShoppingBagIcon}
                tone="violet"
                label="Live Time Orders"
                value={Number(liveTimeReports.ordercount || 0).toLocaleString(
                  'en-US',
                )}
              />
              <StatCard
                icon={HandThumbUpIcon}
                tone="emerald"
                label="Avg Tips"
                value={`${formatCurrency(liveTimeReports.avgtips)} (%${(
                  liveTimeReports.avgtiprate || 0
                ).toFixed(2)})`}
              />
              <StatCard
                icon={CalculatorIcon}
                tone="amber"
                label="Avg Ticket Size"
                value={formatCurrency(liveTimeReports.avgticketsize)}
              />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <SectionCard title="Total Orders by Company" noBodyPadding>
                <div className="p-4">
                  <ReactApexChart
                    options={totalOrdersPieChart.chartOptions}
                    series={totalOrdersPieChart.series}
                    type="pie"
                    height={350}
                  />
                </div>
              </SectionCard>
              <SectionCard title="Total Sales by Company" noBodyPadding>
                <div className="p-4">
                  <ReactApexChart
                    options={totalSalesPieChart.chartOptions}
                    series={totalSalesPieChart.series}
                    type="pie"
                    height={350}
                  />
                </div>
              </SectionCard>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LiveTimeReport;
